import { useMemo } from "react";

import { useMyPermissions } from "../api/queries";

/**
 * Result of {@link usePermissions} — the loaded permission set plus a
 * point-membership predicate used to gate UI entry points.
 *
 * Gating is an experience-layer concern only: hiding or disabling an action a
 * user lacks the permission for. The backend remains the sole enforcement
 * authority, so a permission check passing here is never a substitute for the
 * server-side check.
 */
export interface UsePermissionsResult {
  /** jerry.* role names held by the current user. */
  roles: string[];
  /** jerry.* permission-point strings granted to the current user. */
  permissions: string[];
  /** True while the permission set is being fetched. */
  isLoading: boolean;
  /** True if the permission fetch failed. */
  isError: boolean;
  /**
   * Whether the current user holds a given permission point
   * (e.g. "jerry.conflict:create"). Returns false while loading or on error so
   * gated actions stay hidden until permissions are known.
   */
  hasPermission: (point: string) => boolean;
}

/**
 * Load the current user's jerry.* permission points (cached via react-query)
 * and expose a {@link UsePermissionsResult.hasPermission} predicate for
 * gating UI entry points.
 */
export function usePermissions(): UsePermissionsResult {
  const { data, isLoading, isError } = useMyPermissions();

  const permissions = useMemo(() => data?.permissions ?? [], [data]);
  const roles = useMemo(() => data?.roles ?? [], [data]);

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  const hasPermission = useMemo(
    () => (point: string) => permissionSet.has(point),
    [permissionSet],
  );

  return { roles, permissions, isLoading, isError, hasPermission };
}
